import type { HexColor } from '~/_shared/utils/colors/colors.types';
import {
  type BoundaryEditAction, type BoundaryEditManualMethod,
} from '~/boundary/editMode/boundaryEditModal/boundaryEditModal.helpers';
import { type BoundaryEditSubmitModalAction } from '~/boundary/editMode/boundaryEditSubmitModal/boundaryEditSubmitModalAction.enum';
import { type CreateBoundaryGroupFromAIConfig } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import type { CreateOptimizedTerritoriesConfig } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createOptimizedTerritoriesModal/createOptimizedTerritories.types';
import type { BoundaryCombineGeometryRequestItem } from '~/store/boundaries/boundaries.repository';
import {
  type BoundaryGroupId, type BoundaryId, type BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';

export enum BoundarySelectType {
  Click = 'Click',
  HoverIdle = 'HoverIdle',
  HoverActive = 'HoverActive',
  Lasso = 'Lasso',
}

export enum SelectLassoOverlap {
  Partial = 'partial',
  Contains = 'contains',
}

export enum BoundarySelectMode {
  EditBoundaryTerritory = 'EditBoundaryTerritory',
  EditCustomBoundary = 'EditCustomBoundary',
  QuickEditCustomBoundary = 'QuickEditCustomBoundary',
  EditCustomBoundarySubmit = 'EditCustomBoundarySubmit',
  CreateWMSOptimizedTerritoriesSubset = 'CreateWMSOptimizedTerritoriesSubset',
  CreateOptimizedTerritoriesSubset = 'CreateOptimizedTerritoriesSubset',
}

export type BoundarySelectBucket = Readonly<{
  readonly bucketId: number;
  readonly name: string;
  readonly colorInfo: BucketColorInfo;
}>;

type EditBoundaryTerritorySelectionModeData = Readonly<{
  type: BoundarySelectMode.EditBoundaryTerritory;
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
}>;

type EditCustomBoundarySelectionModeData = Readonly<{
  type: BoundarySelectMode.EditCustomBoundary;
  action: BoundaryEditAction;
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
  boundaryId?: BoundaryId;
}>;

type QuickEditCustomBoundarySelectionModeData = Readonly<{
  type: BoundarySelectMode.QuickEditCustomBoundary;
  action: BoundaryEditAction.EditBoundaries;
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
  selectedBucketId: number | null;
  buckets: readonly BoundarySelectBucket[];
}>;

type CreateWMSOptimizedTerritoriesSubsetSelectionModeData = Readonly<{
  type: BoundarySelectMode.CreateWMSOptimizedTerritoriesSubset;
  configuration: CreateBoundaryGroupFromAIConfig;
}>;

type CreateOptimizedTerritoriesSubsetSelectionModeData = Readonly<{
  type: BoundarySelectMode.CreateOptimizedTerritoriesSubset;
  configuration: CreateOptimizedTerritoriesConfig;
}>;

export type EditCustomBoundarySubmitSelectionConfiguration = {
  geometry: BoundaryCombineGeometryRequestItem[];
  baseGeometry?: BoundaryCombineGeometryRequestItem[];
  submitAction: BoundaryEditSubmitModalAction;
  previousBoundaryGroupId?: BoundaryGroupId;
  method: BoundaryEditManualMethod;
};

type EditCustomBoundarySubmitSelectionModeData = Readonly<{
  type: BoundarySelectMode.EditCustomBoundarySubmit;
  boundaryTerritoryGroupId: BoundaryTerritoryGroupId;
  configuration: EditCustomBoundarySubmitSelectionConfiguration;
}>;

export type BucketColorInfo = Readonly<{
  fillColor: HexColor;
  fillOpacity: number;
  borderColor: HexColor;
  borderOpacity: number;
  borderWidth: number;
}>;

type BoundarySelectCommon = Readonly<{
  selectLassoOverlap: SelectLassoOverlap;
  selectType: BoundarySelectType;
  allowMultipleSelection: boolean;
  onSubmit: (selectState: BoundarySelectState) => void;
  onClose: () => void;
  showHiddenElements: boolean;
}>;

export type BoundarySelectInactiveState = BoundarySelectCommon & Readonly<{
  isModalOpen: false;
  isActive: false;
  hoveredBoundaryId: null;
  selectBoundaryGroupId: null;
  selectedBoundaryInfos: null;
  selectionModeData: null;
  isLassoSelectLoading: false;
}>;

type SelectedBoundaryInfo = Readonly<{
  boundaryId: BoundaryId;
  colorInfo: BucketColorInfo;
  selectedBucketId?: number | null;
}>;

type BoundarySelectActiveCommon = BoundarySelectCommon & Readonly<{
  isModalOpen: true;
  isActive: boolean;
  hoveredBoundaryId: BoundaryId | null;
  selectBoundaryGroupId: BoundaryGroupId;
  selectedBoundaryInfos: ReadonlyArray<SelectedBoundaryInfo>;
  isLassoSelectLoading: boolean;
}>;

export type BoundaryTerritorySelectActiveState = BoundarySelectActiveCommon & Readonly<{
  selectionModeData: EditBoundaryTerritorySelectionModeData;
}>;

export type CustomBoundarySelectActiveState = BoundarySelectActiveCommon & Readonly<{
  selectionModeData: EditCustomBoundarySelectionModeData;
}>;

export type CustomBoundaryQuickEditActiveState = BoundarySelectActiveCommon & Readonly<{
  selectionModeData: QuickEditCustomBoundarySelectionModeData;
}>;

export type CreateWMSOptimizedTerritoriesSubsetSelectActiveState = BoundarySelectActiveCommon & Readonly<{
  selectionModeData: CreateWMSOptimizedTerritoriesSubsetSelectionModeData;
}>;

export type CreateOptimizedTerritoriesSubsetSelectActiveState = BoundarySelectActiveCommon & Readonly<{
  selectionModeData: CreateOptimizedTerritoriesSubsetSelectionModeData;
}>;

export type EditCustomBoundarySubmitSelectActiveState = BoundarySelectActiveCommon & Readonly<{
  selectionModeData: EditCustomBoundarySubmitSelectionModeData;
}>;

export type BoundarySelectState = BoundarySelectInactiveState |
  BoundaryTerritorySelectActiveState |
  CustomBoundarySelectActiveState |
  CustomBoundaryQuickEditActiveState |
  CreateWMSOptimizedTerritoriesSubsetSelectActiveState |
  EditCustomBoundarySubmitSelectActiveState |
  CreateOptimizedTerritoriesSubsetSelectActiveState;

export const isBoundarySelectActive = (
  state: BoundarySelectState
): state is (
  BoundaryTerritorySelectActiveState |
  CustomBoundarySelectActiveState |
  CustomBoundaryQuickEditActiveState |
  CreateWMSOptimizedTerritoriesSubsetSelectActiveState |
  CreateOptimizedTerritoriesSubsetSelectActiveState
  ) => state.isActive;

export const isBoundarySelectModalOpen = (
  state: BoundarySelectState
): state is (
  BoundaryTerritorySelectActiveState |
  CustomBoundarySelectActiveState |
  CustomBoundaryQuickEditActiveState |
  CreateWMSOptimizedTerritoriesSubsetSelectActiveState |
  CreateOptimizedTerritoriesSubsetSelectActiveState
  ) => state.isModalOpen;

export const isBoundarySelectEditBoundaryTerritory = (state: BoundarySelectState): state is BoundaryTerritorySelectActiveState =>
  state.selectionModeData?.type === BoundarySelectMode.EditBoundaryTerritory;

export const isBoundarySelectEditCustomBoundary = (state: BoundarySelectState): state is CustomBoundarySelectActiveState =>
  state.selectionModeData?.type === BoundarySelectMode.EditCustomBoundary;

export const isBoundarySelectQuickEditCustomBoundary = (state: BoundarySelectState): state is CustomBoundaryQuickEditActiveState =>
  state.selectionModeData?.type === BoundarySelectMode.QuickEditCustomBoundary;

export const isBoundarySelectEditCustomBoundarySubmit = (state: BoundarySelectState): state is EditCustomBoundarySubmitSelectActiveState =>
  state.selectionModeData?.type === BoundarySelectMode.EditCustomBoundarySubmit;

export const isBoundarySelectCreateWMSOptimizedTerritoriesSubset = (state: BoundarySelectState): state is CreateWMSOptimizedTerritoriesSubsetSelectActiveState =>
  state.selectionModeData?.type === BoundarySelectMode.CreateWMSOptimizedTerritoriesSubset;

export const isBoundarySelectCreateOptimizedTerritoriesSubset = (state: BoundarySelectState): state is CreateOptimizedTerritoriesSubsetSelectActiveState =>
  state.selectionModeData?.type === BoundarySelectMode.CreateOptimizedTerritoriesSubset;

type BoundarySelectUsedForEditingState =
  BoundaryTerritorySelectActiveState |
  CustomBoundarySelectActiveState |
  CustomBoundaryQuickEditActiveState |
  EditCustomBoundarySubmitSelectActiveState;
export const isBoundarySelectUsedForEditing = (state: BoundarySelectState): state is BoundarySelectUsedForEditingState =>
  isBoundarySelectEditBoundaryTerritory(state) ||
  isBoundarySelectEditCustomBoundary(state) ||
  isBoundarySelectQuickEditCustomBoundary(state) ||
  isBoundarySelectEditCustomBoundarySubmit(state);
